import { createSelector } from 'reselect';
import { toValidationErrors } from 'state/secondaryMarket/shared/amend.utils';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { AppState } from 'state/state.model';

import type { GetValidationErrorsFunc } from '../cpOrderForm/cpOrderForm.selectors';
import type { CpOrderStatus } from './order.model';

function getOrderState(state: AppState, tabId: string) {
  return state.secondaryMarket.cpOrder.order[tabId];
}

type GetCpOrderStatusFunc = (appState: AppState, tabId: string) => CpOrderStatus;

export function getOrderId(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.id;
}
export function getCobDate(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.cobDate;
}
export function getExecTime(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.execTime;
}
export function getSettlementDate(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.settlementDate;
}
export function getTradeDate(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.tradeDate;
}
export function getValuationDate(state: AppState, tabId: string): string | undefined {
  return getOrderState(state, tabId)?.valuationDate;
}
export function getOrderValidationErrors(state: AppState, tabId: string): SecondaryMarketFormError[] {
  const validations = getOrderState(state, tabId)?.validations ?? [];
  return toValidationErrors(validations);
}

export const makeGetCpOrderStatus: (
  getCpOrderValidationError: GetValidationErrorsFunc,
) => GetCpOrderStatusFunc = getCpOrderValidationError =>
  createSelector(
    [
      (appState: AppState, tabId: string) => getOrderState(appState, tabId),
      (appState: AppState, tabId: string) => getCpOrderValidationError(appState, tabId),
    ],
    (orderState, errors) => {
      if (errors.filter(e => e.isMandatory).length > 0) {
        return 'NOT_READY';
      }
      if (orderState?.status === undefined) {
        return 'READY';
      }
      switch (orderState.status) {
        case 'DONE':
          return 'ORDER_PLACED';
        case 'PENDING':
          return 'ORDER_PENDING';
        case 'REJECTED':
          return 'ORDER_FAILED';
        default:
          return 'NOT_READY' as never;
      }
    },
  );

export function getOrderErrorMessage(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.errorMessage;
}

export function getOrderTimeout(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.timeout;
}

export function isOrderTimeout(state: AppState, tabId: string) {
  return getOrderState(state, tabId)?.isTimeout;
}
